import { useEffect, useId, useRef, useState } from "react";
import {
  LuCalendarDays,
  LuWrench,
  LuInfo,
  LuGlobe,
  LuMapPin,
  LuPencil,
  LuTrash2,
  LuRotateCcw,
} from "react-icons/lu";
import { RiFileWarningFill } from "react-icons/ri";
import { PageChangeEvent } from "@progress/kendo-react-data-tools";
import { DeleteConfirmModal } from "@/components/shared/DeleteConfirmModal";
import AccessiblePager from "@/components/shared/tables/AccessiblePager";
import type { SystemMessage, MessageType, MessageStatus } from "@/models.ts";
import { deriveMessageStatus, parseBackendDate } from "./MessageStatus";

interface SystemMessagesTableProps {
  messages: SystemMessage[];
  onEdit?: (message: SystemMessage) => void;
  onDelete?: (message: SystemMessage) => void;
  onRestore?: (message: SystemMessage) => void;
}

const PAGE_SIZE = 10;
const TOOLTIP_MAX_WIDTH = 320;
const TOOLTIP_EST_HEIGHT = 96;

const MESSAGE_TYPE_META: Record<
  MessageType,
  {
    icon: React.ComponentType<{ size?: number; color?: string }>;
    color: string;
  }
> = {
  Certification: { icon: LuCalendarDays, color: "#0891b2" },
  Maintenance: { icon: LuWrench, color: "#ea580c" },
  "CAPSDAC Update": { icon: LuInfo, color: "#2563eb" },
  "Action Required": { icon: RiFileWarningFill, color: "#b91c1c" },
};

type DisplayStatus = MessageStatus | "Inactive";

const STATUS_META: Record<
  DisplayStatus,
  { bg: string; color: string; label: string }
> = {
  Live: { bg: "#047857", color: "#fff", label: "Live" },
  Scheduled: { bg: "#1d4ed8", color: "#fff", label: "Scheduled" },
  Past: { bg: "#475569", color: "#fff", label: "Past" },
  Inactive: { bg: "#64748b", color: "#fff", label: "Inactive" },
};

function formatDateRange(start: string, end: string): string {
  const fmt: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "numeric",
    year: "numeric",
  };
  const startStr = parseBackendDate(start).toLocaleDateString("en-US", fmt);
  const endStr = parseBackendDate(end).toLocaleDateString("en-US", fmt);
  return `${startStr} → ${endStr}`;
}

/** Date + time, used inside the Display Window tooltip. */
function formatDateTime(d: Date): string {
  return d.toLocaleString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** Human-readable description of how long the message runs / is left. */
function windowDetail(start: Date, end: Date, status: MessageStatus): string {
  const DAY = 86_400_000;
  const now = Date.now();
  const totalDays = Math.max(1, Math.round((+end - +start) / DAY));
  const plural = (n: number) => (n === 1 ? "" : "s");

  if (status === "Scheduled") {
    const inDays = Math.max(0, Math.ceil((+start - now) / DAY));
    return `starts in ${inDays} day${plural(inDays)} · runs ${totalDays} day${plural(
      totalDays
    )}`;
  }
  if (status === "Past") {
    const agoDays = Math.max(0, Math.floor((now - +end) / DAY));
    return `ended ${agoDays} day${plural(agoDays)} ago · ran ${totalDays} day${plural(
      totalDays
    )}`;
  }
  // Live
  const leftDays = Math.max(0, Math.ceil((+end - now) / DAY));
  return `${leftDays} of ${totalDays} day${plural(totalDays)} left`;
}

/**
 * High-priority messages pin to the top. Within each priority bucket,
 * messages are ordered by start date — most recent first.
 */
function sortMessages(messages: SystemMessage[]): SystemMessage[] {
  return [...messages].sort((a, b) => {
    if (a.highPriority !== b.highPriority) {
      return a.highPriority ? -1 : 1;
    }
    return (
      parseBackendDate(b.startDate).getTime() -
      parseBackendDate(a.startDate).getTime()
    );
  });
}

export default function SystemMessagesTable({
  messages,
  onEdit,
  onDelete,
  onRestore,
}: SystemMessagesTableProps) {
  const [pendingDelete, setPendingDelete] = useState<SystemMessage | null>(
    null
  );
  const [skip, setSkip] = useState(0);

  const sortedMessages = sortMessages(messages);

  const hasActions = !!(onEdit || onDelete || onRestore);

  const messagesKey = messages.map((m) => m.id).join("|");
  useEffect(() => {
    setSkip(0);
  }, [messagesKey]);

  const maxSkip = Math.max(
    0,
    (Math.ceil(sortedMessages.length / PAGE_SIZE) - 1) * PAGE_SIZE
  );
  const safeSkip = Math.min(skip, maxSkip);
  const pageMessages = sortedMessages.slice(safeSkip, safeSkip + PAGE_SIZE);

  const handleConfirmDelete = () => {
    if (pendingDelete && onDelete) {
      onDelete(pendingDelete);
      setPendingDelete(null);
    }
  };

  if (messages.length === 0) {
    return (
      <div
        style={{
          padding: "3rem 1rem",
          textAlign: "center",
          color: "#64748b",
          backgroundColor: "#fff",
          border: "1px solid #e5e7eb",
          borderRadius: "0.5rem",
        }}
      >
        No messages to display.
      </div>
    );
  }

  return (
    <>
      <div
        style={{
          border: "1px solid #e5e7eb",
          borderRadius: "0.5rem",
          overflow: "hidden",
          backgroundColor: "#fff",
        }}
      >
        <table
          className="table mb-0"
          style={{ tableLayout: "auto", marginBottom: 0 }}
        >
          <thead>
            <tr style={{ background: "var(--color-p3, #1e293b)" }}>
              <th scope="col" style={headerCellStyle()}>
                Label
              </th>
              <th scope="col" style={headerCellStyle()}>
                Title
              </th>
              <th scope="col" style={headerCellStyle()}>
                Scope
              </th>
              <th scope="col" style={headerCellStyle()}>
                Display Window
              </th>
              <th scope="col" style={headerCellStyle()}>
                Status
              </th>
              {hasActions && (
                <th scope="col" style={headerCellStyle()}>
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {pageMessages.map((message, index) => {
              const status = deriveMessageStatus(message);

              const displayStatus: DisplayStatus =
                message.active === false ? "Inactive" : status;

              return (
                <tr
                  key={message.id}
                  style={{
                    backgroundColor: index % 2 === 0 ? "#fff" : "#f8fafc",
                  }}
                >
                  <td style={bodyCellStyle}>
                    <LabelCell type={message.messageType} />
                  </td>

                  <td
                    style={{
                      ...bodyCellStyle,
                      fontWeight: 500,
                      color: "#0f172a",
                    }}
                  >
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: "0.5rem",
                      }}
                    >
                      <span>{message.title}</span>
                      {message.highPriority && <HighPriorityBadge />}
                    </span>
                  </td>

                  <td style={bodyCellStyle}>
                    <ScopeCell
                      global={message.global}
                      leaIds={message.leaIds}
                    />
                  </td>

                  <td
                    style={{
                      ...bodyCellStyle,
                      whiteSpace: "nowrap",
                    }}
                  >
                    <DisplayWindowCell
                      start={message.startDate}
                      end={message.endDate}
                      status={status}
                      displayStatus={displayStatus}
                    />
                  </td>

                  <td style={bodyCellStyle}>
                    <StatusBadge status={displayStatus} />
                  </td>

                  {hasActions && (
                    <td
                      style={{
                        ...bodyCellStyle,
                        whiteSpace: "nowrap",
                      }}
                    >
                      <ActionButtons
                        onEdit={onEdit ? () => onEdit(message) : undefined}
                        onDelete={
                          onDelete ? () => setPendingDelete(message) : undefined
                        }
                        onRestore={
                          onRestore ? () => onRestore(message) : undefined
                        }
                      />
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>

        <AccessiblePager
          skip={safeSkip}
          take={PAGE_SIZE}
          total={sortedMessages.length}
          type="numeric"
          previousNext={true}
          info={true}
          onPageChange={(e: PageChangeEvent) => setSkip(e.skip)}
        />
      </div>

      {pendingDelete && (
        <DeleteConfirmModal
          title="Are you sure you want to delete this message?"
          message={`This will delete "${pendingDelete.title}". It will still be viewable in the Inactive tab.`}
          onCancel={() => setPendingDelete(null)}
          onConfirm={handleConfirmDelete}
        />
      )}
    </>
  );
}

function LabelCell({ type }: { type: MessageType }) {
  const meta = MESSAGE_TYPE_META[type];
  const Icon = meta.icon;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "0.5rem",
        color: "#1e293b",
      }}
    >
      <Icon size={16} color={meta.color} aria-hidden="true" />
      {type}
    </span>
  );
}

function ScopeCell({ global, leaIds }: { global: boolean; leaIds?: string[] }) {
  if (global) {
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "0.375rem",
          color: "#475569",
        }}
      >
        <LuGlobe size={14} color="#64748b" aria-hidden="true" />
        Global
      </span>
    );
  }
  const count = leaIds?.length ?? 0;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "0.375rem",
        color: "#475569",
      }}
    >
      <LuMapPin size={14} color="#64748b" aria-hidden="true" />
      {count} {count === 1 ? "LEA" : "LEAs"}
    </span>
  );
}

function DisplayWindowCell({
  start,
  end,
  status,
  displayStatus,
}: {
  start: string;
  end: string;
  status: MessageStatus;
  displayStatus: DisplayStatus;
}) {
  const [coords, setCoords] = useState<{
    left: number;
    top: number;
    placement: "below" | "above";
  } | null>(null);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const tipId = useId();

  const show = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const left = Math.min(rect.left, window.innerWidth - TOOLTIP_MAX_WIDTH - 8);

    const gap = 6;
    const placeAbove =
      rect.bottom + gap + TOOLTIP_EST_HEIGHT > window.innerHeight &&
      rect.top - gap - TOOLTIP_EST_HEIGHT > 0;
    setCoords({
      left: Math.max(8, left),
      top: placeAbove ? rect.top - gap : rect.bottom + gap,
      placement: placeAbove ? "above" : "below",
    });
  };
  const hide = () => setCoords(null);

  useEffect(() => {
    if (!coords) return;
    const dismiss = () => setCoords(null);
    window.addEventListener("scroll", dismiss, true);
    window.addEventListener("resize", dismiss);
    return () => {
      window.removeEventListener("scroll", dismiss, true);
      window.removeEventListener("resize", dismiss);
    };
  }, [coords]);

  const startDate = parseBackendDate(start);
  const endDate = parseBackendDate(end);
  const isInactive = displayStatus === "Inactive";
  const detail = windowDetail(startDate, endDate, status);
  const heading = isInactive
    ? STATUS_META.Inactive.label
    : `${STATUS_META[status].label} · ${detail}`;

  return (
    <span
      ref={triggerRef}
      tabIndex={0}
      aria-describedby={coords ? tipId : undefined}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          hide();
          triggerRef.current?.blur();
        }
      }}
      style={{
        color: "#475569",
        cursor: "default",
        borderRadius: "2px",
        outlineOffset: "2px",
      }}
    >
      {formatDateRange(start, end)}
      {coords && (
        <span
          id={tipId}
          role="tooltip"
          style={{
            position: "fixed",
            left: coords.left,
            top: coords.top,
            transform:
              coords.placement === "above" ? "translateY(-100%)" : undefined,
            zIndex: 2000,
            maxWidth: TOOLTIP_MAX_WIDTH,
            background: "var(--color-p2, #003a5d)",
            color: "#fff",
            fontSize: "0.8125rem",
            lineHeight: 1.5,
            padding: "8px 12px",
            borderRadius: "8px",
            boxShadow: "0 6px 18px rgba(0, 0, 0, 0.22)",
            pointerEvents: "none",
            whiteSpace: "normal",
          }}
        >
          <span
            style={{
              display: "block",
              fontWeight: 700,
              fontSize: "0.875rem",
              marginBottom: "6px",
            }}
          >
            {heading}
          </span>
          <span
            style={{
              display: "grid",
              gridTemplateColumns: "auto 1fr",
              columnGap: "12px",
              rowGap: "3px",
            }}
          >
            <span style={{ opacity: 0.75 }}>Starts</span>
            <span>{formatDateTime(startDate)}</span>
            <span style={{ opacity: 0.75 }}>Ends</span>
            <span>{formatDateTime(endDate)}</span>
          </span>
        </span>
      )}
    </span>
  );
}

function StatusBadge({ status }: { status: DisplayStatus }) {
  const meta = STATUS_META[status];
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "0.25rem 0.625rem",
        borderRadius: "0.375rem",
        backgroundColor: meta.bg,
        color: meta.color,
        fontSize: "0.75rem",
        fontWeight: 600,
        lineHeight: 1.4,
      }}
    >
      {meta.label}
    </span>
  );
}

function HighPriorityBadge() {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "0.125rem 0.5rem",
        borderRadius: "0.25rem",
        backgroundColor: "#fee2e2",
        color: "#b91c1c",
        border: "1px solid #fecaca",
        fontSize: "0.6875rem",
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.025em",
        lineHeight: 1.4,
        whiteSpace: "nowrap",
      }}
    >
      High Priority
    </span>
  );
}

function ActionButtons({
  onEdit,
  onDelete,
  onRestore,
}: {
  onEdit?: () => void;
  onDelete?: () => void;
  onRestore?: () => void;
}) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: "1rem" }}>
      {onEdit && (
        <button
          type="button"
          onClick={onEdit}
          style={iconButtonStyle("#475569")}
          aria-label="Edit message"
        >
          <LuPencil size={14} aria-hidden="true" />
          Edit
        </button>
      )}
      {onDelete && (
        <button
          type="button"
          onClick={onDelete}
          style={iconButtonStyle("#dc2626")}
          aria-label="Delete message"
        >
          <LuTrash2 size={14} aria-hidden="true" />
          Delete
        </button>
      )}
      {onRestore && (
        <button
          type="button"
          onClick={onRestore}
          style={iconButtonStyle("#047857")}
          aria-label="Restore message"
        >
          <LuRotateCcw size={14} aria-hidden="true" />
          Restore
        </button>
      )}
    </span>
  );
}

function headerCellStyle(width?: string): React.CSSProperties {
  return {
    padding: "14px 24px",
    fontWeight: 500,
    fontSize: "0.875rem",
    color: "#fff",
    background: "var(--color-p3, #1e293b)",
    borderBottom: "none",
    textAlign: "left",
    whiteSpace: "nowrap",
    width,
  };
}

const bodyCellStyle: React.CSSProperties = {
  padding: "14px 24px",
  verticalAlign: "middle",
  fontSize: "0.875rem",
  color: "#334155",
  borderTop: "1px solid #e5e7eb",
};

function iconButtonStyle(color: string): React.CSSProperties {
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.25rem",
    background: "transparent",
    border: "none",
    color,
    fontSize: "0.875rem",
    fontWeight: 500,
    cursor: "pointer",
    padding: "0.25rem 0.25rem",
  };
}
